import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FileText, Upload, Camera, Loader2, CheckCircle2, AlertTriangle, Clock, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_help-beacon-9/artifacts/ok1e76vb_17%20apr%202026%2C%2009_06_17.png";

export default function DocumentUploadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fronteFile, setFronteFile] = useState(null);
  const [retroFile, setRetroFile] = useState(null);
  const [frontePreview, setFrontePreview] = useState(null);
  const [retroPreview, setRetroPreview] = useState(null);

  const token = localStorage.getItem('lifelink_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!user || user === false) { navigate('/login'); return; }
    const fetchStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/document-status`, { headers });
        setStatus(data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleFileSelect = (side, file) => {
    if (!file) return;
    if (side === 'fronte') {
      setFronteFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFrontePreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setFrontePreview(null);
      }
    } else {
      setRetroFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setRetroPreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setRetroPreview(null);
      }
    }
  };

  const handleSubmit = async () => {
    if (!fronteFile || !retroFile) {
      toast.error('Carica entrambi i lati del documento');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('fronte', fronteFile);
      formData.append('retro', retroFile);
      await axios.post(`${API}/auth/upload-documento`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento caricato con successo!');
      // Refresh status
      const { data } = await axios.get(`${API}/auth/document-status`, { headers });
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8F4FD] pt-20 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  const isVerified = status?.documento_verificato;

  return (
    <div className="min-h-screen bg-[#E8F4FD] pt-20 pb-8" data-testid="document-upload-page">
      <div className="max-w-2xl mx-auto px-4">
        <div className="border border-[#B8D4E8] bg-white p-8">
          <div className="flex items-center justify-center mb-6">
            <img src={LOGO} alt="LIFE LINKE" className="h-16 w-auto" />
          </div>

          <h1 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-[#0D1B2A] text-center mb-2">
            <FileText size={18} className="inline mr-2 text-[#FF3B30]" />
            Verifica Documento d'Identità
          </h1>

          {/* Status info */}
          {!isVerified && status?.giorni_rimanenti !== undefined && (
            <div className={`mt-4 p-4 border flex items-center gap-3 ${
              status.scaduto
                ? 'border-[#FF3B30]/30 bg-[#FF3B30]/5'
                : 'border-[#FF9F0A]/30 bg-[#FF9F0A]/5'
            }`} data-testid="deadline-banner">
              {status.scaduto ? (
                <AlertTriangle size={20} className="text-[#FF3B30] flex-shrink-0" />
              ) : (
                <Clock size={20} className="text-[#FF9F0A] flex-shrink-0" />
              )}
              <div>
                <p className="text-sm font-bold" style={{ color: status.scaduto ? '#FF3B30' : '#FF9F0A' }}>
                  {status.scaduto
                    ? 'Termine scaduto! Carica il documento per continuare ad utilizzare il servizio.'
                    : `Hai ancora ${status.giorni_rimanenti} giorni per caricare il documento d'identità.`
                  }
                </p>
                <p className="text-xs text-[#4A6B8A] mt-1">
                  Carica fronte e retro della carta d'identità o passaporto per verificare il tuo account.
                </p>
              </div>
            </div>
          )}

          {isVerified ? (
            <div className="mt-6 text-center space-y-4" data-testid="document-verified">
              <div className="w-20 h-20 bg-[#32D74B]/10 border border-[#32D74B]/30 flex items-center justify-center mx-auto">
                <CheckCircle2 size={40} className="text-[#32D74B]" />
              </div>
              <p className="text-lg font-bold text-[#0D1B2A]">Documento verificato</p>
              <p className="text-sm text-[#4A6B8A]">Il tuo documento d'identità è stato caricato con successo.</p>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#FF1A1A] transition-all"
                data-testid="back-home-btn"
              >
                Torna alla Home
              </button>
            </div>
          ) : (
            <div className="mt-6 space-y-6">
              {/* Fronte */}
              <div data-testid="upload-fronte-section">
                <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-3 font-bold">
                  Fronte del documento
                </label>
                <div
                  className={`border-2 border-dashed p-6 text-center transition-all cursor-pointer ${
                    fronteFile ? 'border-[#32D74B] bg-[#32D74B]/5' : 'border-[#B8D4E8] bg-[#F0F7FC] hover:border-[#FF3B30]'
                  }`}
                  onClick={() => document.getElementById('fronte-input').click()}
                >
                  {frontePreview ? (
                    <div className="relative">
                      <img src={frontePreview} alt="Fronte" className="max-h-48 mx-auto border border-[#B8D4E8]" />
                      <button
                        onClick={(e) => { e.stopPropagation(); setFronteFile(null); setFrontePreview(null); }}
                        className="absolute top-1 right-1 p-1 bg-[#FF3B30] text-white"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : fronteFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText size={20} className="text-[#32D74B]" />
                      <span className="text-sm text-[#32D74B]">{fronteFile.name}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload size={28} className="mx-auto text-[#B8D4E8]" />
                      <p className="text-xs text-[#4A6B8A] uppercase tracking-widest">Clicca o trascina la foto del fronte</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => document.getElementById('fronte-input').click()}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-[#B8D4E8] bg-white text-[#0D1B2A] text-xs uppercase tracking-widest hover:border-[#FF3B30] transition-all"
                    data-testid="fronte-upload-btn"
                  >
                    <Upload size={12} /> Carica
                  </button>
                  <button
                    onClick={() => document.getElementById('fronte-camera').click()}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-[#B8D4E8] bg-white text-[#0D1B2A] text-xs uppercase tracking-widest hover:border-[#FF3B30] transition-all"
                    data-testid="fronte-camera-btn"
                  >
                    <Camera size={12} /> Scatta
                  </button>
                </div>
                <input id="fronte-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleFileSelect('fronte', e.target.files[0])} />
                <input id="fronte-camera" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFileSelect('fronte', e.target.files[0])} />
              </div>

              {/* Retro */}
              <div data-testid="upload-retro-section">
                <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-3 font-bold">
                  Retro del documento
                </label>
                <div
                  className={`border-2 border-dashed p-6 text-center transition-all cursor-pointer ${
                    retroFile ? 'border-[#32D74B] bg-[#32D74B]/5' : 'border-[#B8D4E8] bg-[#F0F7FC] hover:border-[#FF3B30]'
                  }`}
                  onClick={() => document.getElementById('retro-input').click()}
                >
                  {retroPreview ? (
                    <div className="relative">
                      <img src={retroPreview} alt="Retro" className="max-h-48 mx-auto border border-[#B8D4E8]" />
                      <button
                        onClick={(e) => { e.stopPropagation(); setRetroFile(null); setRetroPreview(null); }}
                        className="absolute top-1 right-1 p-1 bg-[#FF3B30] text-white"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : retroFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText size={20} className="text-[#32D74B]" />
                      <span className="text-sm text-[#32D74B]">{retroFile.name}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload size={28} className="mx-auto text-[#B8D4E8]" />
                      <p className="text-xs text-[#4A6B8A] uppercase tracking-widest">Clicca o trascina la foto del retro</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => document.getElementById('retro-input').click()}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-[#B8D4E8] bg-white text-[#0D1B2A] text-xs uppercase tracking-widest hover:border-[#FF3B30] transition-all"
                    data-testid="retro-upload-btn"
                  >
                    <Upload size={12} /> Carica
                  </button>
                  <button
                    onClick={() => document.getElementById('retro-camera').click()}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-[#B8D4E8] bg-white text-[#0D1B2A] text-xs uppercase tracking-widest hover:border-[#FF3B30] transition-all"
                    data-testid="retro-camera-btn"
                  >
                    <Camera size={12} /> Scatta
                  </button>
                </div>
                <input id="retro-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleFileSelect('retro', e.target.files[0])} />
                <input id="retro-camera" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFileSelect('retro', e.target.files[0])} />
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={uploading || !fronteFile || !retroFile}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#FF3B30] text-white font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all disabled:opacity-40 text-sm"
                data-testid="submit-document-btn"
              >
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                Invia Documento
              </button>
              <p className="text-[10px] text-[#6B8AB5] text-center uppercase tracking-widest">
                I tuoi documenti sono protetti e trattati in conformità al GDPR
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
